/**
 * 面试官设置 —— 一张表单,3 件事:
 *   头像:上传一张图(没传 → 用默认蓝底白字)
 *   姓名:文本
 *   声音:展示当前音色 + ▶ 试听
 */

import { useEffect, useRef, useState } from 'react';
import { Avatar } from '../components/Avatar';
import {
  listInterviewers, resetInterviewerAvatar, updateInterviewer, uploadInterviewerAvatar,
} from '../api/interview';
import type { Interviewer } from '../api/types';

// 系统当前音色 —— 不让用户在 UI 改,系统就用这个
const CURRENT_VOICE_LABEL = '沉稳男声';
const CURRENT_VOICE_SAMPLE = '/voice_samples/male_sober.wav';

export default function AdminInterviewers() {
  const [iv, setIv] = useState<Interviewer | null>(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const refresh = async () => {
    try {
      setLoading(true);
      const list = await listInterviewers();
      const target = list.find((i) => i.is_default) || list[0];
      if (!target) throw new Error('系统未初始化面试官');
      setIv(target);
      setName(target.name);
      setError(null);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { void refresh(); }, []);

  const dirty = !!iv && name !== iv.name;

  const handleSaveName = async () => {
    if (!iv || !dirty) return;
    try {
      setBusy(true);
      const updated = await updateInterviewer(iv.id, { name });
      setIv(updated);
      setSavedAt(Date.now());
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  };

  const handleUpload = async (file: File) => {
    if (!iv) return;
    try {
      setBusy(true);
      const updated = await uploadInterviewerAvatar(iv.id, file);
      setIv(updated);
      setSavedAt(Date.now());
    } catch (e: any) { alert('上传失败: ' + e.message); }
    finally { setBusy(false); }
  };

  const handleResetAvatar = async () => {
    if (!iv) return;
    if (!confirm('恢复默认头像(蓝底姓名首字)?')) return;
    try {
      setBusy(true);
      const updated = await resetInterviewerAvatar(iv.id);
      setIv(updated);
    } catch (e: any) { alert(e.message); }
    finally { setBusy(false); }
  };

  const playSample = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (playing) { setPlaying(false); return; }
    const a = new Audio(CURRENT_VOICE_SAMPLE);
    audioRef.current = a;
    setPlaying(true);
    a.onended = () => { setPlaying(false); audioRef.current = null; };
    a.onerror = () => { setPlaying(false); audioRef.current = null; };
    a.play().catch(() => setPlaying(false));
  };

  if (loading) return <div className="container narrow"><p className="hint">加载中...</p></div>;
  if (error && !iv) return <div className="container narrow"><div className="banner error">{error}</div></div>;
  if (!iv) return null;

  const hasCustomAvatar = !!iv.avatar && (iv.avatar.startsWith('/') || iv.avatar.startsWith('http'));

  return (
    <div className="container narrow">
      <div className="page-header">
        <div>
          <h1 className="title">面试官设置</h1>
          <div className="subtitle">改了之后,所有新建面试都用新设置(已有面试不变)</div>
        </div>
      </div>

      {error && <div className="banner error">{error}</div>}
      {savedAt && Date.now() - savedAt < 3000 && (
        <div className="banner success">已保存</div>
      )}

      <div className="card">
        {/* 候选人侧预览 */}
        <div className="row" style={{
          padding: 16, background: 'var(--bg-elev)', borderRadius: 8, marginBottom: 24,
          gap: 16,
        }}>
          <Avatar value={iv.avatar} name={name} size={64} />
          <div>
            <div className="hint">候选人会看到</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>面试官 · {name}</div>
          </div>
        </div>

        {/* 头像 */}
        <div style={{ marginBottom: 24 }}>
          <div className="hint" style={{ marginBottom: 8 }}>头像</div>
          <div className="row" style={{ gap: 12, alignItems: 'center' }}>
            <Avatar value={iv.avatar} name={name} size={64} />
            <div className="row" style={{ gap: 6 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleUpload(f);
                  e.target.value = '';
                }}
              />
              <button onClick={() => fileInputRef.current?.click()} disabled={busy}>
                {hasCustomAvatar ? '更换图片' : '上传图片'}
              </button>
              {hasCustomAvatar && (
                <button className="ghost" onClick={handleResetAvatar} disabled={busy}>
                  恢复默认
                </button>
              )}
            </div>
          </div>
          <div className="hint" style={{ marginTop: 6 }}>
            JPG / PNG / WEBP,2MB 以内 · 不传则使用默认(蓝底 + 姓名首字)
          </div>
        </div>

        {/* 姓名 */}
        <div style={{ marginBottom: 24 }}>
          <div className="hint" style={{ marginBottom: 6 }}>姓名</div>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={20} />
          <div className="hint" style={{ marginTop: 4 }}>
            候选人会听到 AI 自报姓名,建议姓 + 称呼(如:李先生)
          </div>
        </div>

        {/* 声音 —— 单行,只展示 + 试听 */}
        <div style={{ marginBottom: 24 }}>
          <div className="hint" style={{ marginBottom: 6 }}>声音</div>
          <div className="row" style={{
            padding: '10px 12px', background: 'var(--bg-elev)', borderRadius: 8,
            justifyContent: 'space-between',
          }}>
            <div style={{ fontWeight: 500 }}>{CURRENT_VOICE_LABEL}</div>
            <button className="ghost icon" onClick={playSample}>
              {playing ? '⏸ 停止' : '▶ 试听'}
            </button>
          </div>
        </div>

        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button onClick={handleSaveName} disabled={!dirty || busy}>
            {busy ? '保存中...' : dirty ? '保存姓名' : '已保存'}
          </button>
        </div>
      </div>
    </div>
  );
}
